import { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  BarChart3,
  Bot,
  BookOpen,
  Building2,
  Check,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  Clock3,
  Database,
  Gauge,
  Globe2,
  Handshake,
  Lightbulb,
  LineChart,
  LockKeyhole,
  MapPinned,
  Minus,
  MousePointerClick,
  Radio,
  RefreshCw,
  Scale,
  ScanSearch,
  Search,
  ShieldCheck,
  ShoppingBag,
  SlidersHorizontal,
  Sparkles,
  Store,
  Target,
  TrendingDown,
  TrendingUp,
  UserCheck,
  Users,
  Zap,
} from "lucide-react";
import {
  buildChannelCombination,
  DIGITAL_CHANNEL_IDS,
  getChannelControlPresets,
  getChannelDecisionProfile,
  simulateChannelControl,
} from "./channel-strategy";

const STAGES = [
  ["01", "生意卡在哪"],
  ["02", "给三个数"],
  ["03", "看 AI 发现"],
  ["04", "选办法"],
  ["05", "算一算"],
  ["06", "定下一步"],
  ["07", "配渠道"],
  ["08", "看组合"],
  ["09", "控增长"],
];

const OBJECTIVES = {
  MORE_CUSTOMERS: {
    label: "更多有效客户",
    short: "有效客户不足",
    description: "不是只要更多流量，而是增加真正可能成交的客户。",
    icon: Users,
    headline: "先别急着加流量，先确认进来的客户够不够准",
    breakpoint: "理解你",
    breakpointIndex: 1,
    judgment: "当前更值得验证的不是继续扩大曝光，而是目标客户是否在关键触点识别出与你相关的价值。",
    basis: [
      ["事实", "公开内容持续更新，但不同渠道的有效咨询口径尚未统一。"],
      ["推断", "曝光到有效进入之间可能存在较大的客户损耗。"],
      ["待确认", "近一个完整经营周期的渠道投入、有效线索和成交质量。"],
    ],
    primary: "客户数量",
    accepted: "短期不盲目扩大总流量",
    planA: {
      title: "提高有效进入质量",
      summary: "收紧目标客户与内容入口，让更多进入者符合成交条件。",
      metric: "有效进入占比",
      change: "先调整高意向触点与客户筛选条件",
      constraint: "总线索量可能暂时下降",
      risk: "筛选过窄导致潜在客户流失",
      stop: "有效进入提升，但有效成交数量连续恶化",
    },
    planB: {
      title: "扩展高相关触点",
      summary: "复制已出现有效客户的内容与渠道组合，再增加覆盖。",
      metric: "新增有效客户数",
      change: "增加经过验证的相似内容与渠道",
      constraint: "需要额外内容与投放资源",
      risk: "历史有效触点无法复制",
      stop: "新增投入未带来有效进入增长",
    },
  },
  FASTER_CHOICE: {
    label: "更快完成选择",
    short: "客户迟迟不行动",
    description: "缩短从了解、比较到购买或签约的等待。",
    icon: Gauge,
    headline: "客户不是没兴趣，而是还没有足够理由现在行动",
    breakpoint: "相信你",
    breakpointIndex: 2,
    judgment: "客户已经表现出兴趣，但下一步风险不够清楚，选择时间可能损耗在信任证明与行动门槛之间。",
    basis: [
      ["事实", "存在重复咨询与多轮比较行为。"],
      ["推断", "客户不是没有需求，而是仍未获得足够的决策确定性。"],
      ["待确认", "从首次有效接触到成交的时间、主要异议和流失节点。"],
    ],
    primary: "选择效率",
    accepted: "不以更强承诺换取短期速度",
    planA: {
      title: "补齐决策证据",
      summary: "在客户最犹豫的触点前置案例、边界与风险说明。",
      metric: "有效选择率",
      change: "重排关键证据与风险解释",
      constraint: "需要复核案例与承诺边界",
      risk: "信息过多反而延长选择",
      stop: "选择周期缩短但退款或投诉恶化",
    },
    planB: {
      title: "缩短行动路径",
      summary: "减少无意义步骤，让已确认意向的客户更快完成下一动作。",
      metric: "中位选择周期",
      change: "合并重复确认与低价值环节",
      constraint: "组织响应必须同步",
      risk: "过度简化导致客户信息不足",
      stop: "行动率提高但有效成交质量下降",
    },
  },
  LOWER_COST: {
    label: "更低选择成本",
    short: "获客成本上升",
    description: "减少无效投入，让每一笔费用更接近有效成交。",
    icon: CircleDollarSign,
    headline: "先别急着砍预算，先找出哪些钱没有带来有效客户",
    breakpoint: "采取行动",
    breakpointIndex: 3,
    judgment: "成本上升更像是客户进入质量与行动路径的共同损耗，而不是预算本身过高。",
    basis: [
      ["事实", "多个渠道持续产生访问与互动，但尚未统一到有效客户口径。"],
      ["推断", "预算可能被消耗在低意向进入和未完成下一动作的人群上。"],
      ["待确认", "按渠道拆分的投入、有效线索数与后续成交质量。"],
    ],
    primary: "选择成本",
    accepted: "短期不盲目扩大规模，必须观察客户质量",
    planA: {
      title: "先提高有效进入质量",
      summary: "把预算从高流量低意向触点移向高相关客户入口。",
      metric: "单个有效客户成本",
      change: "重分配渠道与内容组合",
      constraint: "总访问量可能下降",
      risk: "成本下降但成交质量恶化",
      stop: "有效 CAC 下降但退款率上升超过护栏",
    },
    planB: {
      title: "先缩短关键转化路径",
      summary: "保持渠道投入，减少客户从兴趣到行动之间的损耗。",
      metric: "客户选择率",
      change: "优化关键页面、咨询与成交衔接",
      constraint: "需要销售与运营共同执行",
      risk: "转化变快但毛利被折扣侵蚀",
      stop: "选择率提升但单客贡献持续下降",
    },
  },
  HIGHER_QUALITY: {
    label: "更高客户质量",
    short: "成交增加但质量变差",
    description: "提高毛利、留存与履约匹配，减少退款和投诉。",
    icon: BadgeCheck,
    headline: "成交数量不是唯一问题，部分成交可能正在损失利润",
    breakpoint: "完成购买",
    breakpointIndex: 4,
    judgment: "当前增长损耗可能不在成交数量，而在客户承诺、产品适配和实际贡献之间存在偏差。",
    basis: [
      ["事实", "成交数量无法单独解释退款、毛利与后续留存。"],
      ["推断", "部分客户被转化，但并不符合高质量成交条件。"],
      ["待确认", "按客户来源拆分的毛利、退款、投诉与复购。"],
    ],
    primary: "选择质量",
    accepted: "短期成交速度和数量可能放缓",
    planA: {
      title: "前置质量筛选",
      summary: "在成交前识别不适配客户，减少后续退款与履约损耗。",
      metric: "高质量成交占比",
      change: "增加适配条件与风险确认",
      constraint: "短期成交数量可能减少",
      risk: "筛选标准误伤潜在优质客户",
      stop: "退款改善但贡献客户数量明显恶化",
    },
    planB: {
      title: "修正价值与承诺",
      summary: "让客户预期与实际交付更一致，减少错误选择。",
      metric: "退款与投诉率",
      change: "修正文案、销售承诺与交付边界",
      constraint: "需要跨部门统一口径",
      risk: "价值表达过弱影响正常转化",
      stop: "投诉下降但有效选择率持续低于基线",
    },
  },
  MORE_RETENTION: {
    label: "更多复购 / 续约",
    short: "复购或续约不足",
    description: "让已成交客户继续选择，而不是重复从零获客。",
    icon: RefreshCw,
    headline: "不要重复购买新流量，先找出老客户为什么不再回来",
    breakpoint: "再次选择",
    breakpointIndex: 5,
    judgment: "首次成交已经发生，但客户未持续感知下一次选择的价值，问题更接近价值兑现与再次触发。",
    basis: [
      ["事实", "首次交易与后续选择需要分别观察。"],
      ["推断", "复购触发、价值感知或续约证明可能没有形成闭环。"],
      ["待确认", "客户分群、首次价值、复购间隔、续约原因与流失原因。"],
    ],
    primary: "客户质量",
    accepted: "不以过度优惠制造短期复购",
    planA: {
      title: "强化首次价值",
      summary: "让客户更早获得可感知结果，为再次选择建立理由。",
      metric: "首次价值完成率",
      change: "重排成交后的关键服务与触点",
      constraint: "需要产品与服务协同",
      risk: "资源集中但未触达真正驱动因素",
      stop: "首次价值改善但复购意向没有变化",
    },
    planB: {
      title: "建立再次选择触发",
      summary: "按客户状态设置复购或续约触点，而不是统一促销。",
      metric: "复购 / 续约率",
      change: "按价值节点触发差异化行动",
      constraint: "需要客户分层和可追踪事件",
      risk: "触达频率过高造成反感",
      stop: "触达增加但退订、投诉或折扣成本恶化",
    },
  },
  NOT_SURE: {
    label: "我不确定问题在哪",
    short: "先找最大损耗",
    description: "不需要先判断原因，览极会从客户看到你到付款的全过程开始检查。",
    icon: ScanSearch,
    headline: "先别猜原因，览极会先找出客户流失最大的一步",
    breakpoint: "待定位",
    breakpointIndex: -1,
    judgment: "目前还不能判断是流量、转化、成本还是客户质量的问题。先统一三个经营数，再找出损耗最大的环节。",
    basis: [
      ["事实", "企业已经感受到增长压力，但问题位置尚未被数据确认。"],
      ["推断", "多个环节可能同时损耗，直接选渠道容易把预算投向错误位置。"],
      ["待确认", "同一周期的获客投入、有效客户、成交数量与退款或流失。"],
    ],
    primary: "先找最大损耗",
    accepted: "在问题位置确认前不追加大额投入",
    planA: {
      title: "先找最大的客户流失点",
      summary: "把客户从看到、了解、咨询到成交的数量放在同一条链上比较。",
      metric: "各环节客户留存率",
      change: "统一客户路径与统计口径",
      constraint: "需要补齐至少一个完整周期的数据",
      risk: "不同部门口径不一致导致误判",
      stop: "数据仍不可比，无法确认主要损耗位置",
    },
    planB: {
      title: "先检查最贵的获客渠道",
      summary: "按渠道比较投入、有效客户和成交质量，先排除明显浪费。",
      metric: "单个有效客户成本",
      change: "拆分渠道投入与成交结果",
      constraint: "暂不判断品牌或内容的长期价值",
      risk: "短周期低估品牌渠道的后续贡献",
      stop: "渠道归因无法达到基本可信程度",
    },
  },
};

const choicePath = ["看到你", "理解你", "相信你", "采取行动", "完成购买", "再次选择"];

const channelCatalogs = {
  commerce: [
    {
      id: "DOUYIN",
      icon: Radio,
      name: "抖音",
      role: "扩大触达并产生即时兴趣",
      method: "短视频内容、达人合作、直播或店播承接",
      metric: "有效咨询 / 进店成本",
      publicData: "内容频率、互动、搜索结果、竞品内容",
      customerData: "投放金额、有效咨询、成交与退款",
      stop: "播放和互动上涨，但有效客户没有同步增加",
    },
    {
      id: "XHS",
      icon: Search,
      name: "小红书",
      role: "建立口碑并影响购买比较",
      method: "场景内容、用户证据、搜索词与产品比较",
      metric: "高意向搜索与有效进入",
      publicData: "笔记、搜索词、互动、竞品口碑",
      customerData: "种草后进店、咨询、成交与客群质量",
      stop: "搜索和收藏增加，但后续有效进入没有变化",
    },
    {
      id: "BAIDU",
      icon: Search,
      name: "百度搜索",
      role: "承接已经主动寻找方案的客户",
      method: "品牌词、品类词、问题词与落地页承接",
      metric: "高意向线索成本",
      publicData: "搜索结果、关键词覆盖、竞品占位",
      customerData: "关键词投入、有效线索、成交和毛利",
      stop: "点击成本可控，但有效线索或成交持续不足",
    },
    {
      id: "WECHAT",
      icon: Users,
      name: "微信生态",
      role: "持续承接、成交和复购",
      method: "公众号、视频号、企微、社群与会员触达",
      metric: "有效选择率 / 复购率",
      publicData: "公开文章、视频号内容与品牌触点",
      customerData: "加微、咨询、成交、复购与退订",
      stop: "触达增加，但成交、复购或客户满意度恶化",
    },
    {
      id: "TRANSACTION",
      icon: ShoppingBag,
      name: "平台电商",
      role: "承接站内搜索、推荐和即时成交",
      method: "商品页、站内搜索、推荐、活动与客服承接",
      metric: "成交率、退款率与单客贡献",
      publicData: "商品、价格、评价与公开促销",
      customerData: "访问、订单、实付、退款和复购",
      stop: "成交率提高，但退款、毛利或复购明显恶化",
    },
    {
      id: "DEEP_CONTENT",
      icon: BookOpen,
      name: "B站 / 知乎",
      role: "用深度内容解释产品价值与专业差异",
      method: "测评、问答、教程、案例和长周期搜索内容",
      metric: "深度内容到有效咨询率",
      publicData: "视频、回答、搜索排名、互动和竞品议题",
      customerData: "内容来源、咨询、成交周期和成交质量",
      stop: "内容观看和收藏增加，但有效咨询持续不足",
    },
    {
      id: "OWNED_SITE",
      icon: Globe2,
      name: "官网 / SEO / GEO",
      role: "承接搜索引擎和 AI 问答带来的主动需求",
      method: "优化官网内容、落地页和结构化信息，让搜索与 AI 答案更容易发现、理解和引用",
      metric: "搜索 / AI 引荐到有效成交率",
      publicData: "网站、收录、关键词、页面、结构化信息、AI 答案引用和竞品可见性",
      customerData: "访问来源、AI 引荐、咨询、订单和归因窗口",
      stop: "搜索或 AI 可见性增加，但有效咨询或成交没有改善",
    },
    {
      id: "OFFLINE_DISTRIBUTION",
      icon: Building2,
      name: "线下门店 / 经销",
      role: "通过真实体验、门店网络和合作终端成交",
      method: "门店陈列、体验、导购、经销商和区域活动",
      metric: "到店成交率 / 单店贡献",
      publicData: "门店、经销覆盖、公开活动和竞品网点",
      customerData: "客流、到店来源、成交、库存、毛利和退货",
      stop: "覆盖或客流增加，但单店贡献和库存效率恶化",
    },
  ],
  retail: [
    {
      id: "DOUYIN_LOCAL",
      icon: Radio,
      name: "抖音本地生活",
      role: "让附近客户发现门店并产生到店意愿",
      method: "门店内容、达人探店、直播与团购承接",
      metric: "有效到店成本",
      publicData: "门店内容、团购、互动与竞店表现",
      customerData: "投放、核销、到店成交与退款",
      stop: "团购和曝光增加，但有效到店或毛利恶化",
    },
    {
      id: "LOCAL_REPUTATION",
      icon: Store,
      name: "口碑与到店搜索",
      role: "影响客户比较、导航和到店选择",
      method: "小红书场景内容、评价管理与门店搜索承接",
      metric: "搜索到店率",
      publicData: "评价、搜索结果、内容和竞店口碑",
      customerData: "到店来源、核销、客单与复购",
      stop: "收藏和导航增加，但真实到店没有变化",
    },
    {
      id: "BAIDU_MAP",
      icon: Search,
      name: "百度搜索 / 地图",
      role: "承接明确的附近需求与品牌查找",
      method: "品牌词、品类词、地图信息和门店页优化",
      metric: "搜索到店成本",
      publicData: "搜索占位、门店信息、竞店和评价",
      customerData: "电话、导航、到店与消费数据",
      stop: "搜索动作增加，但门店有效客流未改善",
    },
    {
      id: "WECHAT_MEMBER",
      icon: Users,
      name: "微信会员",
      role: "促进老客户再次到店和复购",
      method: "企微、会员、社群、优惠与价值节点触达",
      metric: "会员复购率",
      publicData: "公众号、视频号和公开活动",
      customerData: "会员分层、触达、复购、客单和退订",
      stop: "触达频次提高，但复购、客单或满意度下降",
    },
    {
      id: "POS",
      icon: ShoppingBag,
      name: "外卖 / 本地生活平台",
      role: "承接附近需求、团购核销和即时交易",
      method: "门店页、团购、外卖、评价和平台活动承接",
      metric: "核销成本 / 平台净贡献",
      publicData: "门店、团购、评价、价格和竞店表现",
      customerData: "平台投入、订单、核销、佣金、退款和毛利",
      stop: "平台订单增加，但佣金后贡献或复购持续恶化",
    },
    {
      id: "RETAIL_ECOMMERCE",
      icon: ShoppingBag,
      name: "平台电商",
      role: "扩大门店之外的商品交易和区域覆盖",
      method: "站内搜索、商品页、活动、直播和会员承接",
      metric: "平台成交成本 / 净贡献",
      publicData: "商品、价格、评价、活动和竞品店铺",
      customerData: "访问、广告、订单、佣金、退款和毛利",
      stop: "线上订单增加，但净贡献或线下业务明显恶化",
    },
    {
      id: "RETAIL_PARTNER",
      icon: Handshake,
      name: "异业合作 / 商圈渠道",
      role: "通过商圈、企业和合作品牌获得新增客流",
      method: "联名、企业团购、商圈活动和合作权益",
      metric: "合作渠道有效到店成本",
      publicData: "合作活动、商圈信息和公开客群信号",
      customerData: "合作投入、券码、核销、客单和复购",
      stop: "核销增加，但新增客户、客单或复购没有改善",
    },
    {
      id: "STORE_NETWORK",
      icon: Building2,
      name: "门店自然客流",
      role: "优化网点、陈列、导购和店内成交",
      method: "门店触点、动线、导购、商品组合和现场转化",
      metric: "进店成交率 / 单店贡献",
      publicData: "网点、商圈、竞店、价格和公开评价",
      customerData: "客流、成交、客单、毛利、库存和会员",
      stop: "进店或成交改善，但毛利、库存或体验恶化",
    },
  ],
  b2b: [
    {
      id: "BAIDU_B2B",
      icon: Search,
      name: "百度搜索",
      role: "承接正在主动寻找解决方案的企业",
      method: "问题词、品类词、案例词与落地页线索承接",
      metric: "有效商机成本",
      publicData: "关键词、搜索结果、竞品占位与内容",
      customerData: "投放、有效线索、商机、合同与周期",
      stop: "线索增加，但有效商机率或赢单率下降",
    },
    {
      id: "WECHAT_B2B",
      icon: Users,
      name: "微信生态",
      role: "持续培育信任并推进复杂决策",
      method: "公众号、视频号、企微、案例与销售跟进",
      metric: "商机推进率",
      publicData: "公开内容、案例、活动与品牌触点",
      customerData: "加微、会议、商机阶段、赢单与流失原因",
      stop: "触达增加，但商机推进或销售周期没有改善",
    },
    {
      id: "CONTENT_B2B",
      icon: BookOpen,
      name: "B站 / 知乎 / 行业内容",
      role: "解释复杂价值并建立专业可信度",
      method: "行业洞察、案例、白皮书、直播与活动",
      metric: "内容到有效商机率",
      publicData: "内容、互动、引用、竞品议题与搜索表现",
      customerData: "下载、报名、有效线索、商机和赢单",
      stop: "内容触达增加，但目标客户和有效商机没有增加",
    },
    {
      id: "SALES_OUTBOUND",
      icon: Target,
      name: "销售主动触达",
      role: "直接寻找并验证高价值目标客户",
      method: "目标账户、销售触达、诊断会与方案跟进",
      metric: "目标账户转商机率",
      publicData: "企业名单、公开需求、招聘与业务信号",
      customerData: "触达、回复、会议、商机、合同与周期",
      stop: "触达量增加，但回复、会议或有效商机持续不足",
    },
    {
      id: "CRM",
      icon: Globe2,
      name: "官网 / SEO / GEO",
      role: "承接搜索、AI 问答、内容和销售带来的企业客户",
      method: "优化解决方案页、案例和结构化信息，让搜索与 AI 答案准确理解并引用企业能力",
      metric: "官网 / AI 引荐到有效商机率",
      publicData: "网站、案例、关键词、结构化信息、AI 答案引用和竞品可见性",
      customerData: "访问来源、AI 引荐、下载、咨询、商机和合同",
      stop: "搜索或 AI 可见性增加，但有效商机没有改善",
    },
    {
      id: "INDUSTRY_EVENT",
      icon: MapPinned,
      name: "行业活动 / 会议",
      role: "集中触达目标角色并建立高密度信任",
      method: "展会、闭门会、研讨会、演讲和会后跟进",
      metric: "参会目标账户转商机率",
      publicData: "活动、嘉宾、议题、企业和竞品参与",
      customerData: "活动投入、到场、目标账户、会议和商机",
      stop: "到场和名片增加，但有效会议或商机不足",
    },
    {
      id: "CHANNEL_PARTNER",
      icon: Handshake,
      name: "渠道伙伴 / 经销商",
      role: "借助合作伙伴覆盖客户和区域市场",
      method: "伙伴招募、联合方案、转介绍和区域销售",
      metric: "伙伴有效商机 / 渠道贡献",
      publicData: "伙伴网络、区域覆盖和公开合作信息",
      customerData: "伙伴线索、商机、合同、分成和回款",
      stop: "伙伴数量增加，但活跃伙伴和有效商机不足",
    },
  ],
};

function channelCatalogFor(businessModel) {
  if (["B2B 销售", "订阅 SaaS"].includes(businessModel)) return channelCatalogs.b2b;
  if (businessModel === "连锁门店") return channelCatalogs.retail;
  return channelCatalogs.commerce;
}

function channelPriorityIds(objectiveKey, businessModel) {
  const catalog = channelCatalogFor(businessModel);
  const ids = catalog.map((item) => item.id);
  const preferences = {
    MORE_CUSTOMERS: [ids[0], ids[2]],
    FASTER_CHOICE: [ids[3], ids[2]],
    LOWER_COST: [ids[2], ids[4]],
    HIGHER_QUALITY: [ids[4], ids[2]],
    MORE_RETENTION: [ids[3], ids[4]],
    NOT_SURE: [ids[4], ids[2]],
  };
  return preferences[objectiveKey] ?? ids.slice(0, 2);
}

const initialSimulation = {
  traffic: 18000,
  entryRate: 3.2,
  choiceRate: 10.5,
  spend: 120000,
  contribution: 1800,
  refundRate: 5.5,
};

const emptyCustomerInputs = {
  industry: "",
  businessModel: "",
  period: "",
  source: "",
  spend: "",
  effectiveEntries: "",
  customers: "",
  refundRate: "",
  note: "",
  confirmed: false,
};

const demoCustomerInputs = {
  industry: "消费电子与 3C",
  businessModel: "自营电商 / DTC",
  period: "最近一个自然月",
  source: "系统导出 + 人工确认",
  spend: "120000",
  effectiveEntries: "576",
  customers: "60",
  refundRate: "5.5",
  note: "主要渠道有持续访问，但有效客户成本上升，成交质量需要同步观察。",
  confirmed: true,
};

function baselineFromInputs(inputs) {
  const traffic = 18000;
  const effectiveEntries = Math.max(0, Number(inputs.effectiveEntries) || 0);
  const customers = Math.max(0, Number(inputs.customers) || 0);
  return {
    traffic,
    entryRate: traffic > 0 ? Math.min(100, effectiveEntries / traffic * 100) : 0,
    choiceRate: effectiveEntries > 0 ? Math.min(100, customers / effectiveEntries * 100) : 0,
    spend: Math.max(0, Number(inputs.spend) || 0),
    contribution: 1800,
    refundRate: Math.max(0, Number(inputs.refundRate) || 0),
  };
}

function formatNumber(value, digits = 0) {
  return new Intl.NumberFormat("zh-CN", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

function formatCurrency(value) {
  const sign = value < 0 ? "-" : "";
  return `${sign}¥${formatNumber(Math.abs(value))}`;
}

function calculation(values) {
  const effectiveEntries = values.traffic * values.entryRate / 100;
  const customers = effectiveEntries * values.choiceRate / 100;
  const effectiveCac = customers > 0 ? values.spend / customers : 0;
  const contributionResult = customers * values.contribution - values.spend;
  const refunds = customers * values.refundRate / 100;
  return { effectiveEntries, customers, effectiveCac, contributionResult, refunds };
}

export default function ExperienceDemo() {
  const [stage, setStage] = useState(0);
  const [maxStage, setMaxStage] = useState(0);
  const [objectiveKey, setObjectiveKey] = useState("LOWER_COST");
  const [customerInputs, setCustomerInputs] = useState(emptyCustomerInputs);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [sim, setSim] = useState(initialSimulation);
  const [analysing, setAnalysing] = useState(false);
  const [analysisStep, setAnalysisStep] = useState(0);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [selectedCombination, setSelectedCombination] = useState("RECOMMENDED");
  const [control, setControl] = useState({
    totalBudget: 120000,
    primaryShare: 0.65,
    contentShare: 0.45,
    l2Enabled: false,
  });
  const objective = OBJECTIVES[objectiveKey];
  const inputBaseline = useMemo(() => baselineFromInputs(customerInputs), [customerInputs]);

  useEffect(() => {
    document.title = "览极｜AI 增长决策体验";
  }, []);

  const goTo = (next) => {
    setStage(next);
    setMaxStage((current) => Math.max(current, next));
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  const runAnalysis = () => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    setAnalysing(true);
    setAnalysisStep(0);
    setSim(inputBaseline);
    const interval = reduced ? 90 : 850;
    const timerA = window.setTimeout(() => setAnalysisStep(1), interval);
    const timerB = window.setTimeout(() => setAnalysisStep(2), interval * 2);
    const timerC = window.setTimeout(() => setAnalysisStep(3), interval * 3);
    const timerD = window.setTimeout(() => {
      setAnalysing(false);
      goTo(2);
    }, reduced ? interval * 4 : 3600);
    return () => [timerA, timerB, timerC, timerD].forEach(window.clearTimeout);
  };

  const chooseObjective = (key) => {
    setObjectiveKey(key);
    setSelectedPlan("");
    setSelectedChannels([]);
    setSelectedCombination("RECOMMENDED");
    setControl({ totalBudget: 120000, primaryShare: 0.65, contentShare: 0.45, l2Enabled: false });
    setSim(initialSimulation);
  };

  return (
    <div className="experience-demo">
      <DemoHeader />
      <StageRail stage={stage} maxStage={maxStage} onStage={goTo} />
      <main className="demo-main">
        <DemoContext objective={objective} stage={stage} />
        {stage === 0 && (
          <ObjectiveStage
            objectiveKey={objectiveKey}
            objective={objective}
            onChoose={chooseObjective}
            onContinue={() => goTo(1)}
          />
        )}
        {stage === 1 && (
          <DataInputStage
            values={customerInputs}
            onChange={setCustomerInputs}
            onDemo={() => setCustomerInputs(demoCustomerInputs)}
            onBack={() => goTo(0)}
            onContinue={runAnalysis}
          />
        )}
        {stage === 2 && (
          <JudgmentStage
            objective={objective}
            customerInputs={customerInputs}
            onBack={() => goTo(1)}
            onContinue={() => goTo(3)}
          />
        )}
        {stage === 3 && (
          <OptionsStage
            objective={objective}
            selectedPlan={selectedPlan}
            onSelect={setSelectedPlan}
            onBack={() => goTo(2)}
            onContinue={() => {
              setSim(inputBaseline);
              goTo(4);
            }}
          />
        )}
        {stage === 4 && (
          <SimulationStage
            values={sim}
            baseline={inputBaseline}
            onChange={setSim}
            onBack={() => goTo(3)}
            onContinue={() => goTo(5)}
          />
        )}
        {stage === 5 && (
          <DecisionStage
            objective={objective}
            selectedPlan={selectedPlan}
            values={sim}
            onConfirm={() => {
              goTo(6);
            }}
            onBack={() => goTo(4)}
          />
        )}
        {stage === 6 && (
          <ChannelStage
            objectiveKey={objectiveKey}
            objective={objective}
            businessModel={customerInputs.businessModel}
            selectedPlan={selectedPlan}
            selectedChannels={selectedChannels}
            onSelect={(channelId) => {
              setSelectedChannels((current) => {
                if (current.includes(channelId)) return current.filter((id) => id !== channelId);
                if (current.length >= 2) return current;
                return [...current, channelId];
              });
            }}
            onConfirm={() => {
              setSelectedCombination("RECOMMENDED");
              goTo(7);
            }}
            onBack={() => goTo(5)}
          />
        )}
        {stage === 7 && (
          <CombinationStrategyStage
            objective={objective}
            selectedChannels={selectedChannels}
            selectedOption={selectedCombination}
            onSelect={setSelectedCombination}
            onBack={() => goTo(6)}
            onContinue={() => {
              const preset = selectedCombination === "LOW_RISK"
                ? { primaryShare: 0.55, contentShare: 0.65 }
                : selectedCombination === "NO_ACTION"
                  ? { primaryShare: 0.5, contentShare: 1 }
                  : { primaryShare: 0.65, contentShare: 0.45 };
              setControl((current) => ({ ...current, ...preset }));
              goTo(8);
            }}
          />
        )}
        {stage === 8 && (
          <GrowthControlStage
            objective={objective}
            selectedChannels={selectedChannels}
            selectedOption={selectedCombination}
            baseline={inputBaseline}
            control={control}
            onChange={setControl}
            onBack={() => goTo(7)}
            onRestart={() => {
              setStage(0);
              setMaxStage(0);
              setCustomerInputs(emptyCustomerInputs);
              setSelectedPlan("");
              setSelectedChannels([]);
              setSelectedCombination("RECOMMENDED");
              setControl({ totalBudget: 120000, primaryShare: 0.65, contentShare: 0.45, l2Enabled: false });
              setSim(initialSimulation);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
        )}
      </main>
      <DemoFooter />
      {analysing && <AnalysisOverlay step={analysisStep} objective={objective} />}
    </div>
  );
}

function DemoHeader() {
  return (
    <header className="demo-header">
      <a className="demo-brand" href="/demo" aria-label="览极 AI 系统">
        <span>览极</span><i /><strong>AI 系统</strong>
      </a>
      <div className="demo-header-center">
        <span>AI 增长检查</span>
        <small>找损耗 · 选办法 · 定渠道</small>
      </div>
      <div className="demo-status"><i />系统就绪</div>
    </header>
  );
}

function StageRail({ stage, maxStage, onStage }) {
  return (
    <nav className="demo-stage-rail" aria-label="体验进度">
      <div className="demo-stage-inner">
        {STAGES.map(([number, label], index) => (
          <button
            key={number}
            className={index === stage ? "active" : index < stage ? "complete" : ""}
            disabled={index > maxStage}
            onClick={() => onStage(index)}
          >
            <span>{index < stage ? <Check size={13} /> : number}</span>
            <strong>{label}</strong>
          </button>
        ))}
      </div>
    </nav>
  );
}

function DemoContext({ objective, stage }) {
  return (
    <div className="demo-context">
      <div>
        <small>本次演示目标</small>
        <strong>{objective.label}</strong>
      </div>
      <div>
        <small>览极当前在做什么</small>
        <strong>{["确认生意卡点", "计算三个经营数", "说明 AI 发现", "比较解决办法", "试算可能影响", "确认优先路径", "选择双渠道", "生成组合策略", "形成增长控制卡"][stage]}</strong>
      </div>
      <span><Sparkles size={14} />所有数值均为演示情景</span>
    </div>
  );
}

function ObjectiveStage({ objectiveKey, objective, onChoose, onContinue }) {
  return (
    <section className="demo-stage objective-stage">
      <div className="stage-kicker growth-ai-kicker">
        <Sparkles size={16} />
        <strong>览极 AI 系统</strong>
        <span>正在检查企业增长</span>
      </div>
      <div className="stage-title-row">
        <div>
          <h1><span className="growth-title-accent">览极 AI 系统</span>，先帮你找出增长卡点</h1>
          <p>你不需要先判断原因。览极会检查客户在哪里流失、钱可能浪费在哪里，并把问题继续落到解决办法、渠道和数据。</p>
        </div>
        <span className="stage-count">1 / 9</span>
      </div>

      <div className="objective-question">
        <small>第一步只需要完成一件事</small>
        <strong>你现在最想解决哪个增长问题？</strong>
      </div>

      <div className="objective-grid">
        {Object.entries(OBJECTIVES).map(([key, item]) => {
          const Icon = item.icon;
          return (
            <button key={key} className={objectiveKey === key ? "selected" : ""} onClick={() => onChoose(key)}>
              <span className="objective-icon"><Icon size={19} /></span>
              <span>
                <strong>{item.label}</strong>
                <small>{item.description}</small>
              </span>
              <i>{objectiveKey === key ? <Check size={13} /> : <ChevronRight size={14} />}</i>
            </button>
          );
        })}
      </div>

      <div className="product-explainer">
        <article>
          <span><Database size={16} />览极有什么</span>
          <strong>先帮你查公开信息</strong>
          <p>查市场、渠道、内容、搜索和竞品，不让你从零填写。</p>
        </article>
        <article>
          <span><UserCheck size={16} />览极要什么</span>
          <strong>只要三个经营数</strong>
          <p>花了多少钱、来了多少有效客户、最后成交多少。</p>
        </article>
        <article>
          <span><BarChart3 size={16} />览极看什么</span>
          <strong>钱花在哪、客户掉在哪</strong>
          <p>同时检查客户数量、成交速度、获客成本和成交质量。</p>
        </article>
        <article>
          <span><MousePointerClick size={16} />你会得到</span>
          <strong>先做什么、去哪个渠道做</strong>
          <p>给出办法、渠道、具体动作、观察数据和停止条件。</p>
        </article>
      </div>

      <div className="stage-action-bar">
        <div>
          <small>已选择</small>
          <strong>{objective.label}</strong>
          <span>览极接下来会先检查：{objective.short}</span>
        </div>
        <button className="demo-primary" onClick={onContinue}>
          下一步：告诉览极三个数 <ArrowRight size={17} />
        </button>
      </div>
    </section>
  );
}

function DataInputStage({ values, onChange, onDemo, onBack, onContinue }) {
  const requiredKeys = ["industry", "businessModel", "period", "source", "spend", "effectiveEntries", "customers"];
  const completed = requiredKeys.filter((key) => String(values[key]).trim()).length + (values.confirmed ? 1 : 0);
  const total = requiredKeys.length + 1;
  const ready = completed === total;
  const completeness = Math.round(completed / total * 100);
  const setValue = (key, value) => onChange({ ...values, [key]: value });
  const isB2B = ["B2B 销售", "订阅 SaaS"].includes(values.businessModel);
  const isStore = values.businessModel === "连锁门店";
  const entryLabel = isB2B ? "有多少条有效线索" : isStore ? "有多少位有效到店客户" : "有多少人认真咨询 / 进店 / 留资";
  const customerLabel = isB2B ? "其中多少个最终签约" : isStore ? "其中多少位最终消费" : "其中多少人最终成交";

  return (
    <section className="demo-stage data-input-stage">
      <div className="stage-kicker"><Database size={16} />不用交一堆表，先给三个数</div>
      <div className="stage-title-row">
        <div>
          <h1>告诉览极：花了多少钱、来了多少客户、成交了多少</h1>
          <p>三个数必须属于同一段时间。估算也可以，览极会标记可信程度，不会把估算当成真实结果。</p>
        </div>
        <button className="demo-load-button" onClick={onDemo}><Sparkles size={14} />载入演示数据</button>
      </div>

      <div className="data-input-layout">
        <section className="auto-prefill-panel">
          <div className="block-heading">
            <div><small>览极先帮你做</small><h2>这些信息不用你手工填写</h2></div>
            <span className="source-tag">公开 / 授权信号</span>
          </div>
          <div className="prefill-status">
            <article>
              <span><CheckCircle2 size={15} />公开触点</span>
              <strong>品牌内容、搜索结果和渠道入口</strong>
              <small>公开信息可以由系统自动收集</small>
            </article>
            <article>
              <span><CheckCircle2 size={15} />市场与竞品</span>
              <strong>行业动向和主要竞品表现</strong>
              <small>公开证据只能支持初步判断</small>
            </article>
            <article>
              <span><Clock3 size={15} />你需要确认</span>
              <strong>真实投入、有效客户和成交</strong>
              <small>这是公开网络无法知道的部分</small>
            </article>
          </div>
          <div className="input-boundary-note">
            <ShieldCheck size={16} />
            <p><strong>为什么还要三个数</strong>：网络能看到声量和内容，但看不到你真实花了多少钱、成交了多少客户。</p>
          </div>
        </section>

        <section className="customer-input-panel">
          <div className="input-progress">
            <div>
              <small>三个数准备情况</small>
              <strong>{completeness}%</strong>
            </div>
            <div className="input-progress-track"><i style={{ width: `${completeness}%` }} /></div>
            <span>{ready ? "可以开始首轮检查" : `还差 ${total - completed} 项`}</span>
          </div>

          <div className="business-context-grid">
            <SelectField label="主营行业" value={values.industry} onChange={(value) => setValue("industry", value)}>
              <option value="">请选择</option>
              <option>餐饮与连锁门店</option>
              <option>零售电商</option>
              <option>消费电子与 3C</option>
              <option>企业服务 / SaaS</option>
              <option>制造业</option>
              <option>其他</option>
            </SelectField>
            <SelectField label="业务模式" value={values.businessModel} onChange={(value) => setValue("businessModel", value)}>
              <option value="">请选择</option>
              <option>自营电商 / DTC</option>
              <option>平台电商</option>
              <option>连锁门店</option>
              <option>B2B 销售</option>
              <option>订阅 SaaS</option>
              <option>混合模式</option>
            </SelectField>
            <SelectField label="观察周期" value={values.period} onChange={(value) => setValue("period", value)}>
              <option value="">请选择</option>
              <option>最近四个完整周</option>
              <option>最近一个自然月</option>
              <option>最近一个完整季度</option>
              <option>其他可比周期</option>
            </SelectField>
            <SelectField label="数据来源" value={values.source} onChange={(value) => setValue("source", value)}>
              <option value="">请选择</option>
              <option>系统导出 + 人工确认</option>
              <option>财务 / 销售台账</option>
              <option>人工估算</option>
              <option>暂不确定</option>
            </SelectField>
          </div>

          <div className="core-data-heading">
            <div><small>最重要的输入</small><h2>请按同一段时间填写</h2></div>
            <span>不要混用不同月份</span>
          </div>
          <div className="core-number-grid">
            <NumberField label="这段时间获客花了多少钱" value={values.spend} prefix="¥" placeholder="例如 120000" onChange={(value) => setValue("spend", value)} />
            <NumberField label={entryLabel} value={values.effectiveEntries} suffix="个" placeholder="例如 576" onChange={(value) => setValue("effectiveEntries", value)} />
            <NumberField label={customerLabel} value={values.customers} suffix="个" placeholder="例如 60" onChange={(value) => setValue("customers", value)} />
          </div>

          <div className="optional-input-row">
            <NumberField label="退款 / 取消率（质量护栏）" value={values.refundRate} suffix="%" placeholder="可选，例如 5.5" onChange={(value) => setValue("refundRate", value)} />
            <label className="note-field">
              <span>还有什么明显不对劲（可选）</span>
              <textarea value={values.note} placeholder="例如：咨询不少，但最近越来越难成交" onChange={(event) => setValue("note", event.target.value)} />
            </label>
          </div>

          <label className={values.confirmed ? "data-confirmation checked" : "data-confirmation"}>
            <input type="checkbox" checked={values.confirmed} onChange={(event) => setValue("confirmed", event.target.checked)} />
            <span>{values.confirmed ? <Check size={14} /> : null}</span>
            <p><strong>我确认这些数字属于同一段时间</strong><small>估算可以提交，览极会提示后续需要核验。</small></p>
          </label>
        </section>
      </div>

      <div className="data-output-preview">
        <div><small>览极马上会算</small><strong>每个成交客户花多少钱、100 个有效客户能成交多少</strong></div>
        <div><small>览极还会判断</small><strong>钱更可能浪费在哪里，以及下一步最该先检查什么</strong></div>
      </div>

      <StageNavigation
        onBack={onBack}
        onContinue={onContinue}
        continueLabel={ready ? "开始 AI 检查" : `还需完成 ${total - completed} 项`}
        disabled={!ready}
      />
    </section>
  );
}

function SelectField({ label, value, onChange, children }) {
  return (
    <label className="select-field">
      <span>{label}<i>必填</i></span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>{children}</select>
    </label>
  );
}

function NumberField({ label, value, prefix = "", suffix = "", placeholder, onChange }) {
  return (
    <label className="number-field">
      <span>{label}</span>
      <div>{prefix && <i>{prefix}</i>}<input type="number" min="0" value={value} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} />{suffix && <i>{suffix}</i>}</div>
    </label>
  );
}

function JudgmentStage({ objective, customerInputs, onBack, onContinue }) {
  const effectiveEntries = Number(customerInputs.effectiveEntries) || 0;
  const customers = Number(customerInputs.customers) || 0;
  const spend = Number(customerInputs.spend) || 0;
  const choiceRate = effectiveEntries > 0 ? customers / effectiveEntries * 100 : 0;
  const effectiveCac = customers > 0 ? spend / customers : 0;
  const refundRate = Number(customerInputs.refundRate);
  const basisLabels = { "事实": "已经知道", "推断": "览极推测", "待确认": "还要确认" };
  return (
    <section className="demo-stage">
      <div className="stage-kicker"><Bot size={16} />览极先说结论</div>
      <div className="judgment-hero">
        <div>
          <span className="provisional-badge">首轮检查 · 仍需后台数据验证</span>
          <h1>{objective.headline}</h1>
          <p>{objective.judgment}</p>
        </div>
        <div className="confidence-box">
          <small>目前有多可靠</small>
          <strong>中等</strong>
          <span>有三个经营数，仍需分渠道核验</span>
        </div>
      </div>

      <div className="boss-result-strip">
        <article>
          <small>每成交 1 位客户花费</small>
          <strong>{formatCurrency(effectiveCac)}</strong>
          <span>按当前投入 ÷ 成交数计算</span>
        </article>
        <article>
          <small>每 100 个有效客户成交</small>
          <strong>{formatNumber(choiceRate, 1)} 个</strong>
          <span>需要与历史周期或渠道比较</span>
        </article>
        <article className="priority">
          <small>览极建议先做</small>
          <strong>{objective.planA.title}</strong>
          <span>先验证，再决定是否扩大投入</span>
        </article>
      </div>

      <div className="decision-path-block">
        <div className="choice-model-intro">
          <div>
            <small>览极客户选择模型</small>
            <h2>增长发生在客户一步步选择你的过程中</h2>
            <p>增长不只是获得更多流量，而是让更多合适的客户，从看到你、理解你、相信你，到完成购买，并愿意再次选择。</p>
          </div>
          <div className="choice-model-focus">
            <small>本轮最可能的增长损耗</small>
            <strong>{objective.breakpoint}</strong>
            <span>{objective.breakpointIndex < 0 ? "需要补齐客户路径数据后定位" : "客户停下的位置，就是下一步优先验证的位置"}</span>
          </div>
        </div>
        <div className="choice-path">
          {choicePath.map((item, index) => (
            <div key={item} className={index === objective.breakpointIndex ? "breakpoint" : index < objective.breakpointIndex ? "passed" : ""}>
              <span>{index < objective.breakpointIndex ? <Check size={14} /> : index + 1}</span>
              <strong>{item}</strong>
              {index < choicePath.length - 1 && <ChevronRight className="path-arrow" size={17} />}
            </div>
          ))}
        </div>
      </div>

      <div className="judgment-grid">
        <section className="evidence-ledger">
          <div className="block-heading">
            <div><small>为什么这么说</small><h2>已经知道什么，还要确认什么</h2></div>
          </div>
          {objective.basis.map(([type, text], index) => (
            <article key={type} className={index === 0 ? "fact" : index === 1 ? "inference" : "missing"}>
              <span>{basisLabels[type]}</span>
              <p>{text}</p>
            </article>
          ))}
          <article className="customer-fact">
            <span>你的数字</span>
            <p>{customerInputs.period}：每 100 个有效客户约成交 {formatNumber(choiceRate, 1)} 个，每个成交客户约花 {formatCurrency(effectiveCac)}。</p>
          </article>
          <div className="weakest-assumption">
            <Lightbulb size={16} />
            <p><strong>最容易判断错的地方</strong>：这些总数可能掩盖不同渠道的巨大差别，下一步必须按渠道拆开。</p>
          </div>
        </section>

        <section className="opportunity-map">
          <div className="block-heading">
            <div><small>增长机会图</small><h2>用四项关键结果，判断增长是否健康</h2></div>
          </div>
          {[
            ["来了多少有效客户", `${formatNumber(effectiveEntries)} 个`, objective.primary === "客户数量", "客户数量"],
            ["100 个能成交多少", `${formatNumber(choiceRate, 1)} 个`, objective.primary === "选择效率", "成交效率"],
            ["每成交一个花多少", formatCurrency(effectiveCac), objective.primary === "选择成本", "获客成本"],
            ["成交质量是否健康", Number.isFinite(refundRate) && customerInputs.refundRate !== "" ? `退款 / 取消 ${formatNumber(refundRate, 1)}%` : "还没有数据", objective.primary === "客户质量" || objective.primary === "选择质量", "质量护栏"],
          ].map(([name, value, active, role]) => (
            <article key={name} className={active ? "priority" : ""}>
              <div><span>{name}</span>{active && <strong>先改善这个</strong>}</div>
              <p className="opportunity-value">{value}</p>
              <small>{role} · {active ? "本轮优先验证" : "同步观察，防止顾此失彼"}</small>
            </article>
          ))}
        </section>
      </div>

      <StageNavigation onBack={onBack} onContinue={onContinue} continueLabel="看看有哪些解决办法" />
    </section>
  );
}

function OptionsStage({ objective, selectedPlan, onSelect, onBack, onContinue }) {
  const options = [
    ["A", objective.planA, true],
    ["B", objective.planB, false],
    ["BASELINE", {
      title: "暂不行动，先补基线",
      summary: "不改变现有动作，只补齐关键数据，避免在证据不足时追加资源。",
      metric: "基线完整度",
      change: "保持现状并统一数据口径",
      constraint: "短期不新增增长动作",
      risk: "继续承受当前损耗",
      stop: "核心数据达到可比较条件",
    }, false],
  ];
  return (
    <section className="demo-stage">
      <div className="stage-kicker"><Scale size={16} />览极给你三种处理办法</div>
      <div className="stage-title-row">
        <div>
          <h1>现在选一个最符合企业现实的办法</h1>
          <p>不要只看哪个听起来更好。重点看企业做不做得到、要付出什么，以及什么情况应该立即停下。</p>
        </div>
        <span className="mkt-badge">这轮先保住：{objective.primary}</span>
      </div>

      <div className="option-comparison">
        {options.map(([key, option, recommended]) => (
          <button key={key} className={selectedPlan === key ? "selected" : ""} onClick={() => onSelect(key)}>
            <header>
              <span>{key === "BASELINE" ? <Minus size={16} /> : key}</span>
              {recommended && <small><Sparkles size={12} />览极建议先比较</small>}
              {selectedPlan === key && <i><Check size={14} /></i>}
            </header>
            <h2>{option.title}</h2>
            <p>{option.summary}</p>
            <dl>
              <div><dt>具体做什么</dt><dd>{option.change}</dd></div>
              <div><dt>先看哪个数</dt><dd>{option.metric}</dd></div>
              <div><dt>可能要牺牲</dt><dd>{option.constraint}</dd></div>
              <div><dt>最怕发生什么</dt><dd>{option.risk}</dd></div>
              <div><dt>什么情况马上停</dt><dd>{option.stop}</dd></div>
            </dl>
          </button>
        ))}
      </div>

      <div className="tradeoff-strip">
        <div><small>这轮先改善</small><strong>{objective.primary}</strong></div>
        <ChevronRight size={18} />
        <div><small>可能暂时牺牲</small><strong>{objective.accepted}</strong></div>
        <ChevronRight size={18} />
        <div><small>览极不会说</small><strong>不能把试算结果当成实际增长</strong></div>
      </div>

      <StageNavigation
        onBack={onBack}
        onContinue={onContinue}
        continueLabel={selectedPlan ? "先算一算可能影响" : "请先选择一个办法"}
        disabled={!selectedPlan}
      />
    </section>
  );
}

function SimulationStage({ values, baseline, onChange, onBack, onContinue }) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [activeScenario, setActiveScenario] = useState("current");
  const result = useMemo(() => calculation(values), [values]);
  const baselineResult = useMemo(() => calculation(baseline), [baseline]);
  const setValue = (key, value) => onChange({ ...values, [key]: Number(value) });
  const presets = {
    quality: { ...baseline, entryRate: Math.min(100, baseline.entryRate * 1.1), refundRate: Math.max(0, baseline.refundRate - 0.3) },
    path: { ...baseline, choiceRate: Math.min(100, baseline.choiceRate * 1.1) },
    cost: { ...baseline, spend: Math.round(baseline.spend * 0.9) },
  };
  const applyScenario = (key) => {
    setActiveScenario(key);
    onChange(key === "current" ? baseline : presets[key]);
  };
  return (
    <section className="demo-stage">
      <div className="stage-kicker"><SlidersHorizontal size={16} />先算一算，再决定要不要做</div>
      <div className="stage-title-row">
        <div>
          <h1>如果只改变一个数，可能影响多少客户和费用？</h1>
          <p>先看方向和值不值得验证。这里是试算，不是业绩预测，最终结果必须由真实行动和真实数据确认。</p>
        </div>
        <span className="scenario-badge"><Sparkles size={13} />试算结果，不是增长承诺</span>
      </div>

      <div className="simulator-layout">
        <section className="simulator-controls">
          <div className="simple-scenario-heading">
            <small>不用调复杂参数</small>
            <h2>先选一个最想试算的变化</h2>
          </div>
          <div className="simple-scenario-list">
            <button className={activeScenario === "quality" ? "active" : ""} onClick={() => applyScenario("quality")}>
              <span><Users size={17} /></span><div><strong>认真了解的客户多 10%</strong><small>看有效客户增加后能带来多少成交</small></div><i>{activeScenario === "quality" && <Check size={13} />}</i>
            </button>
            <button className={activeScenario === "path" ? "active" : ""} onClick={() => applyScenario("path")}>
              <span><Gauge size={17} /></span><div><strong>成交效率提高 10%</strong><small>看同样客户量能多成交多少</small></div><i>{activeScenario === "path" && <Check size={13} />}</i>
            </button>
            <button className={activeScenario === "cost" ? "active" : ""} onClick={() => applyScenario("cost")}>
              <span><CircleDollarSign size={17} /></span><div><strong>获客投入减少 10%</strong><small>看客户不变时每个成交成本怎样变化</small></div><i>{activeScenario === "cost" && <Check size={13} />}</i>
            </button>
          </div>
          <button className="advanced-toggle" onClick={() => setAdvancedOpen((current) => !current)}>
            <SlidersHorizontal size={14} />{advancedOpen ? "收起详细调整" : "我想自己调整详细数字"}<ChevronRight size={14} />
          </button>
          {advancedOpen && (
            <div className="advanced-controls">
              <RangeControl label="看到你的人" value={values.traffic} min={5000} max={40000} step={500} suffix="人" onChange={(value) => { setActiveScenario("custom"); setValue("traffic", value); }} />
              <RangeControl label="认真了解的比例" value={values.entryRate} min={1} max={8} step={0.1} suffix="%" onChange={(value) => { setActiveScenario("custom"); setValue("entryRate", value); }} />
              <RangeControl label="最终成交的比例" value={values.choiceRate} min={4} max={25} step={0.5} suffix="%" onChange={(value) => { setActiveScenario("custom"); setValue("choiceRate", value); }} />
              <RangeControl label="获客投入" value={values.spend} min={40000} max={250000} step={5000} prefix="¥" onChange={(value) => { setActiveScenario("custom"); setValue("spend", value); }} />
              <RangeControl label="退款 / 取消率" value={values.refundRate} min={0} max={18} step={0.5} suffix="%" onChange={(value) => { setActiveScenario("custom"); setValue("refundRate", value); }} />
            </div>
          )}
          <button className="restore-baseline" onClick={() => applyScenario("current")}><RefreshCw size={14} />恢复原始数据</button>
        </section>

        <section className="simulation-output">
          <div className="simulation-flow">
            <FlowNode label="看到你的人" value={formatNumber(values.traffic)} />
            <ChevronRight size={17} />
            <FlowNode label="认真了解" value={formatNumber(result.effectiveEntries)} />
            <ChevronRight size={17} />
            <FlowNode label="最终成交" value={formatNumber(result.customers)} active />
          </div>
          <div className="metric-output-grid">
            <MetricOutput icon={Users} label="最终成交客户" value={`${formatNumber(result.customers)} 人`} delta={result.customers - baselineResult.customers} />
            <MetricOutput icon={CircleDollarSign} label="每个成交客户成本" value={formatCurrency(result.effectiveCac)} delta={result.effectiveCac - baselineResult.effectiveCac} currency lowerBetter />
            <MetricOutput icon={LineChart} label="本轮获客投入" value={formatCurrency(values.spend)} delta={values.spend - baseline.spend} currency lowerBetter />
            <MetricOutput icon={ShieldCheck} label="可能退款 / 取消" value={`${formatNumber(result.refunds, 1)} 人`} delta={result.refunds - baselineResult.refunds} lowerBetter />
          </div>
          <div className="simulation-reading">
            <Bot size={18} />
            <div>
              <small>本次试算的决策提示</small>
              <strong>{result.effectiveCac < baselineResult.effectiveCac
                ? "这个变化可能降低每个成交客户的成本，但必须确认成交质量没有变差。"
                : result.customers > baselineResult.customers
                  ? "这个变化可能增加成交客户，但成本没有同步改善，不能只看成交数量。"
                  : "这个变化目前没有改善成交或成本，不值得直接扩大投入。"}</strong>
              <p>下一步进入渠道方案，览极会告诉你在哪个渠道、用什么动作验证这个变化。</p>
            </div>
          </div>
        </section>
      </div>
      <StageNavigation onBack={onBack} onContinue={onContinue} continueLabel="确认下一步优先路径" />
    </section>
  );
}

function RangeControl({ label, value, min, max, step, prefix = "", suffix = "", onChange }) {
  const percent = ((value - min) / (max - min)) * 100;
  return (
    <label className="range-control">
      <span><strong>{label}</strong><output>{prefix}{formatNumber(value, step < 1 ? 1 : 0)}{suffix}</output></span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        style={{ "--range-fill": `${percent}%` }}
        onChange={(event) => onChange(event.target.value)}
      />
      <small><i>{prefix}{formatNumber(min)}{suffix}</i><i>{prefix}{formatNumber(max)}{suffix}</i></small>
    </label>
  );
}

function FlowNode({ label, value, active = false }) {
  return <div className={active ? "flow-node active" : "flow-node"}><small>{label}</small><strong>{value}</strong></div>;
}

function MetricOutput({ icon: Icon, label, value, delta, currency = false, lowerBetter = false }) {
  const favorable = lowerBetter ? delta < -0.001 : delta > 0.001;
  const neutral = Math.abs(delta) <= 0.001;
  return (
    <article>
      <span><Icon size={15} />{label}</span>
      <strong>{value}</strong>
      <small className={neutral ? "neutral" : favorable ? "positive" : "negative"}>
        {neutral ? <Minus size={12} /> : favorable ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
        {neutral ? "与基线一致" : `较基线 ${delta > 0 ? "+" : ""}${currency ? formatCurrency(delta) : formatNumber(delta)}`}
      </small>
    </article>
  );
}

function DecisionStage({ objective, selectedPlan, values, onConfirm, onBack }) {
  const option = selectedPlan === "A" ? objective.planA : selectedPlan === "B" ? objective.planB : {
    title: "暂不行动，先补基线",
    change: "保持现状并统一关键数据口径",
    metric: "基线完整度",
    stop: "核心数据达到可比较条件",
  };
  const result = calculation(values);

  return (
    <section className="demo-stage">
      <div className="stage-kicker"><CheckCircle2 size={16} />先定方向，再进入渠道打法</div>
      <div className="stage-title-row">
        <div>
          <h1>你已经选好先解决什么，下一步把它落到具体渠道</h1>
          <p>这里确认的是优先路径。确认后，览极会继续比较抖音、小红书、百度、微信及交易平台应该分别做什么。</p>
        </div>
        <span className="decision-ready"><i />可以配置渠道</span>
      </div>

      <div className="action-contract">
        <section className="chosen-action">
          <small>你选的解决方向</small>
          <h2>{option.title}</h2>
          <p>{option.change}</p>
          <div className="action-meta">
            <span><small>谁来负责</small><strong>增长负责人（下一步确认）</strong></span>
            <span><small>什么时候开始</small><strong>渠道数据和三个经营数完成核对</strong></span>
            <span><small>什么时候重看</small><strong>渠道动作发生、数据明显变化或证据冲突</strong></span>
          </div>
        </section>
        <section className="metric-contract">
          <div className="block-heading"><div><small>做完看什么</small><h2>四个数防止只看表面增长</h2></div></div>
          {[
            ["最后结果", `成交客户约 ${formatNumber(result.customers)} 人`, "试算"],
            ["成交效率", `每 100 个有效客户成交 ${formatNumber(values.choiceRate, 1)} 个`, "待复算"],
            ["过程变化", `认真了解比例 ${formatNumber(values.entryRate, 1)}%`, "待复算"],
            ["不能变坏", `退款 / 取消率不高于 ${formatNumber(values.refundRate, 1)}%`, "需确认"],
          ].map(([type, value, status]) => (
            <article key={type}>
              <span>{type}</span><strong>{value}</strong><small>{status}</small>
            </article>
          ))}
        </section>
      </div>

      <div className="stop-condition">
        <ShieldCheck size={19} />
        <div><small>停止条件</small><strong>{option.stop}</strong></div>
        <span>触发后停止扩大，不等待固定 30 / 60 / 90 天</span>
      </div>

      <div className="client-system-summary">
        <div><small>方向已经确定</small><strong>{option.title}</strong></div>
        <ChevronRight size={18} />
        <div><small>下一步览极做什么</small><strong>比较渠道、打法、所需数据和风险</strong></div>
        <ChevronRight size={18} />
        <div><small>你最终得到什么</small><strong>一张可以交给团队执行的渠道行动卡</strong></div>
      </div>

      <StageNavigation onBack={onBack} onContinue={onConfirm} continueLabel="确认方向，继续选择渠道" />
    </section>
  );
}

function ChannelStage({
  objectiveKey,
  objective,
  businessModel,
  selectedPlan,
  selectedChannels,
  onSelect,
  onConfirm,
  onBack,
}) {
  const channels = channelCatalogFor(businessModel);
  const priorityIds = channelPriorityIds(objectiveKey, businessModel);
  const selected = selectedChannels.map((id) => channels.find((channel) => channel.id === id)).filter(Boolean);
  const primaryChannel = selected[0];
  const supportChannel = selected[1];
  const option = selectedPlan === "A" ? objective.planA : selectedPlan === "B" ? objective.planB : {
    title: "暂不行动，先补基线",
    metric: "基线完整度",
    stop: "核心数据达到可比较条件",
  };

  return (
    <section className="demo-stage channel-stage">
      <div className="stage-kicker"><Radio size={16} />把解决方向落到增长渠道和具体打法</div>
      <div className="stage-title-row">
        <div>
          <h1>选择一个主渠道，再选择一个协同渠道</h1>
          <p>主渠道负责获得或推动客户，协同渠道负责承接、补证或完成交易。览极下一页会把两个渠道真正连成一条客户路径。</p>
        </div>
        <span className="channel-selection-count">已选 <strong>{selectedChannels.length}</strong> / 2</span>
      </div>

      <div className="channel-logic-strip">
        <div><small>要解决的问题</small><strong>{objective.label}</strong></div>
        <ChevronRight size={18} />
        <div><small>已经选择的办法</small><strong>{option.title}</strong></div>
        <ChevronRight size={18} />
        <div><small>现在要决定</small><strong>主渠道 + 协同渠道</strong></div>
      </div>

      <div className="channel-source-band">
        <span>览极底座渠道来源</span>
        <strong>内容与社媒</strong>
        <strong>搜索 / 官网 / GEO</strong>
        <strong>私域与会员</strong>
        <strong>交易与本地生活</strong>
        <strong>门店 / 经销 / 销售 / 合作</strong>
      </div>

      <div className="channel-grid">
        {channels.map((channel) => {
          const Icon = channel.icon;
          const deepReady = DIGITAL_CHANNEL_IDS.includes(channel.id);
          const priorityIndex = priorityIds.indexOf(channel.id);
          const selectedIndex = selectedChannels.indexOf(channel.id);
          const selectionFull = selectedChannels.length >= 2 && selectedIndex === -1;
          const level = priorityIndex === 0 ? "优先验证" : priorityIndex === 1 ? "可作补充" : "其他可选";
          return (
            <button
              key={channel.id}
              className={selectedIndex >= 0 ? `selected selected-${selectedIndex + 1}` : ""}
              disabled={selectionFull}
              onClick={() => onSelect(channel.id)}
            >
              <header>
                <span><Icon size={18} /></span>
                <div>
                  <h2>{channel.name}</h2>
                  <small>{channel.role}</small>
                  <em className={deepReady ? "deep" : "base"}>{deepReady ? "深度推演" : "基础诊断"}</em>
                </div>
                <b className={selectedIndex === 0 ? "primary-choice" : selectedIndex === 1 ? "support-choice" : priorityIndex === 0 ? "priority" : priorityIndex === 1 ? "support" : ""}>
                  {selectedIndex === 0 ? "主渠道" : selectedIndex === 1 ? "协同渠道" : level}
                </b>
              </header>
              <dl>
                <div><dt>可以怎么做</dt><dd>{channel.method}</dd></div>
                <div><dt>做完先看</dt><dd>{channel.metric}</dd></div>
                <div><dt>企业要给</dt><dd>{channel.customerData}</dd></div>
              </dl>
              <footer>
                <span>{selectedIndex >= 0 ? <Check size={13} /> : <ChevronRight size={14} />}</span>
                {selectedIndex === 0 ? "已选为主渠道" : selectedIndex === 1 ? "已选为协同渠道" : selectionFull ? "已达到两个渠道上限" : "选择这个渠道"}
              </footer>
            </button>
          );
        })}
      </div>

      {primaryChannel && (
        <div className="channel-pair-preview">
          <div>
            <small>主渠道</small>
            <strong>{primaryChannel.name}</strong>
            <span>{primaryChannel.publicData}</span>
          </div>
          <div>
            <small>协同渠道</small>
            <strong>{supportChannel?.name ?? "再选择一个渠道"}</strong>
            <span>{supportChannel?.publicData ?? "第二个渠道将用于形成组合策略"}</span>
          </div>
          <div className={supportChannel ? "ready" : ""}>
            <small>组合策略</small>
            <strong>{supportChannel ? "已具备生成条件" : "等待双渠道选择"}</strong>
            <span>下一页将展示客户转移、内容、投放和三个可选方案</span>
          </div>
        </div>
      )}

      <StageNavigation
        onBack={onBack}
        onContinue={onConfirm}
        continueLabel={selectedChannels.length === 2 ? "生成双渠道组合策略" : `还需选择 ${2 - selectedChannels.length} 个渠道`}
        disabled={selectedChannels.length !== 2}
      />
    </section>
  );
}

function CombinationStrategyStage({
  objective,
  selectedChannels,
  selectedOption,
  onSelect,
  onBack,
  onContinue,
}) {
  const combination = buildChannelCombination(selectedChannels[0], selectedChannels[1], objective);
  if (!combination) return null;
  const option = combination.options.find((item) => item.id === selectedOption) ?? combination.options[0];
  const channels = [
    ["主渠道", selectedChannels[0], combination.primary],
    ["协同渠道", selectedChannels[1], combination.support],
  ];
  const primaryDecision = getChannelDecisionProfile(selectedChannels[0]);
  const supportDecision = getChannelDecisionProfile(selectedChannels[1]);
  const decisionProfiles = [primaryDecision, supportDecision];
  const opinion = selectedOption === "NO_ACTION"
    ? {
      verdict: "本轮不建议增加渠道预算，先把两个渠道的客户标识和结果口径接通。",
      reason: `当前还无法证明${combination.primary.name}与${combination.support.name}谁在带来有效客户。先补基线，比盲目扩量更可靠。`,
      allocation: "不新增预算；只投入数据治理、承接修复和最小内容验证。",
    }
    : selectedOption === "LOW_RISK"
      ? {
        verdict: `本轮不建议双渠道同时扩量。先小批验证${combination.primary.name}，${combination.support.name}只承担承接和补证。`,
        reason: `先确认客户确实能从${combination.primary.name}进入${combination.support.name}，再决定是否增加投入。`,
        allocation: `主渠道 55%，协同渠道 45%；内容 65%，付费投放 35%。`,
      }
      : {
        verdict: `本轮建议由${combination.primary.name}负责验证需求，${combination.support.name}负责承接和补证，不建议五五开。`,
        reason: `${combination.primary.name}先证明能带来有效进入；${combination.support.name}再证明这些客户愿意继续选择。两项都成立后才扩大投入。`,
        allocation: `主渠道 65%，协同渠道 35%；内容 45%，付费投放 55%。`,
      };

  return (
    <section className="demo-stage combination-stage">
      <div className="stage-kicker"><Sparkles size={16} />览极 AI 已把两个渠道连成一条客户选择路径</div>
      <div className="stage-title-row">
        <div>
          <h1>不是同时做两个渠道，而是让它们各自完成一件事</h1>
          <p>先看客户如何从主渠道进入协同渠道，再选择推荐方案、低风险替代或不行动基线。AI 负责比较，最后由企业确认。</p>
        </div>
        <span className="data-level-badge">{combination.strategyLevel === "DEEP" ? "深度推演渠道 · L1" : "基础诊断渠道 · L1"}</span>
      </div>

      <div className="combination-path">
        <article>
          <small>主渠道 · 负责获得或推动</small>
          <strong>{combination.primary.name}</strong>
          <span>{combination.primary.stage}</span>
        </article>
        <div><ArrowRight size={18} /><small>统一客户标识</small></div>
        <article>
          <small>协同渠道 · 负责承接或完成</small>
          <strong>{combination.support.name}</strong>
          <span>{combination.support.stage}</span>
        </article>
      </div>
      <div className="handoff-statement">
        <div><small>客户如何跨渠道</small><strong>{combination.handoff}</strong></div>
        <div><small>如何避免重复算功劳</small><strong>{combination.attribution}</strong></div>
      </div>

      <div className="section-heading">
        <div><small>先决定怎么做</small><h2>三个方案都保留取舍和风险</h2></div>
        <span>当前选择：{option.title}</span>
      </div>
      <div className="combination-options">
        {combination.options.map((item) => (
          <button
            key={item.id}
            className={selectedOption === item.id ? "active" : ""}
            onClick={() => onSelect(item.id)}
          >
            <header><small>{item.label}</small>{selectedOption === item.id && <CheckCircle2 size={17} />}</header>
            <h3>{item.title}</h3>
            <p>{item.summary}</p>
            <dl>
              <div><dt>预算方向</dt><dd>{item.budget}</dd></div>
              <div><dt>内容 / 投放</dt><dd>{item.content}</dd></div>
              <div><dt>主要风险</dt><dd>{item.risk}</dd></div>
            </dl>
          </button>
        ))}
      </div>

      <section className="lanji-channel-opinion">
        <header>
          <div><small>览极明确意见</small><h2>{opinion.verdict}</h2></div>
          <span><Lightbulb size={15} />不是渠道说明，是本轮取舍</span>
        </header>
        <p>{opinion.reason}</p>
        <div className="opinion-decision-grid">
          <article>
            <small>主渠道现在先做</small>
            <strong>{primaryDecision.first}</strong>
          </article>
          <article>
            <small>协同渠道现在先做</small>
            <strong>{supportDecision.first}</strong>
          </article>
          <article className="pause">
            <small>本轮明确暂缓</small>
            <strong>{primaryDecision.pause} {supportDecision.pause}</strong>
          </article>
          <article className="gate">
            <small>达到什么才允许扩大</small>
            <strong>{combination.primary.metric}改善，并且{combination.support.guardrail}不恶化。</strong>
          </article>
        </div>
        <footer>
          <span>资源意见</span>
          <strong>{opinion.allocation}</strong>
        </footer>
      </section>

      <div className="section-heading channel-plan-heading">
        <div><small>再看判断依据</small><h2>为什么这样分工，以及团队具体怎么执行</h2></div>
        <span><ShieldCheck size={14} />不会自动发布或投放</span>
      </div>
      <div className="channel-work-plans">
        {channels.map(([label, id, channel], index) => (
          <section key={id}>
            <header>
              <div><small>{label}</small><h3>{channel.name}</h3></div>
              <span>{channel.metric}</span>
            </header>
            <div className="channel-opinion-strip">
              <small>览极对这个渠道的意见</small>
              <strong>{decisionProfiles[index].stance}</strong>
              <div>
                <span><b>优先</b>{decisionProfiles[index].first}</span>
                <span><b>暂缓</b>{decisionProfiles[index].pause}</span>
                <span><b>放行</b>{decisionProfiles[index].gate}</span>
              </div>
            </div>
            <div className="channel-work-columns">
              <article>
                <h4><BookOpen size={15} />内容怎么做</h4>
                <dl>{channel.content.map(([key, value]) => <div key={key}><dt>{key}</dt><dd>{value}</dd></div>)}</dl>
              </article>
              <article>
                <h4><Target size={15} />{channel.executionLabel ?? "投放怎么做"}</h4>
                <dl>{channel.paid.map(([key, value]) => <div key={key}><dt>{key}</dt><dd>{value}</dd></div>)}</dl>
              </article>
            </div>
            {channel.geo && (
              <div className="geo-readiness">
                <header><Globe2 size={16} /><div><small>GEO 专项</small><strong>让 AI 答案能够准确理解、引用并引荐</strong></div></header>
                <dl>{channel.geo.map(([key, value]) => <div key={key}><dt>{key}</dt><dd>{value}</dd></div>)}</dl>
              </div>
            )}
            <footer><AlertBoundary label="停止条件" value={channel.stop} /></footer>
          </section>
        ))}
      </div>

      <div className="strategy-boundary">
        <div><Database size={17} /><span><small>当前可以输出</small><strong>渠道分工、内容与投放方向、预算比例和补数清单</strong></span></div>
        <div><LockKeyhole size={17} /><span><small>当前不能输出</small><strong>{combination.strategyLevel === "DEEP" ? "客户数、CAC、利润或退款变化区间；这些需要 L2 渠道数据" : "专项数值区间；当前组合含基础诊断渠道，需先完成该渠道的指标与归因适配"}</strong></span></div>
      </div>

      <StageNavigation
        onBack={onBack}
        onContinue={onContinue}
        continueLabel="确认方案，进入增长控制台"
      />
    </section>
  );
}

function AlertBoundary({ label, value }) {
  return <div className="alert-boundary"><ShieldCheck size={14} /><span><small>{label}</small><strong>{value}</strong></span></div>;
}

function GrowthControlStage({
  objective,
  selectedChannels,
  selectedOption,
  baseline,
  control,
  onChange,
  onBack,
  onRestart,
}) {
  const combination = buildChannelCombination(selectedChannels[0], selectedChannels[1], objective);
  if (!combination) return null;
  const l2Available = combination.strategyLevel === "DEEP";
  const l2Enabled = l2Available && control.l2Enabled;
  const selected = combination.options.find((item) => item.id === selectedOption) ?? combination.options[0];
  const channelPlans = [
    { id: selectedChannels[0], role: "主渠道", channel: combination.primary },
    { id: selectedChannels[1], role: "协同渠道", channel: combination.support },
  ].map((plan) => ({ ...plan, levers: getChannelControlPresets(plan.id) }));
  const result = simulateChannelControl({
    baseline,
    totalBudget: control.totalBudget,
    primaryShare: control.primaryShare,
    contentShare: control.contentShare,
    l2Enabled,
    selectedOption,
  });
  const update = (field, value) => onChange((current) => ({ ...current, [field]: value }));
  const leverValue = (channelId, item) => control.channelLevers?.[channelId]?.[item.id] ?? item.defaultValue;
  const updateLever = (channelId, leverId, value) => onChange((current) => ({
    ...current,
    channelLevers: {
      ...(current.channelLevers ?? {}),
      [channelId]: {
        ...(current.channelLevers?.[channelId] ?? {}),
        [leverId]: value,
      },
    },
  }));
  const scenarioValues = result.scenarios ? Object.values(result.scenarios) : [];
  const metricRanges = result.scenarios ? [
    ["有效客户", `${formatNumber(Math.min(...scenarioValues.map((item) => item.customers)))}–${formatNumber(Math.max(...scenarioValues.map((item) => item.customers)))}`, "情景值"],
    ["客户选择率", `${(Math.min(...scenarioValues.map((item) => item.choiceRate)) * 100).toFixed(1)}%–${(Math.max(...scenarioValues.map((item) => item.choiceRate)) * 100).toFixed(1)}%`, "情景值"],
    ["有效 CAC", `${formatCurrency(Math.min(...scenarioValues.map((item) => item.cac)))}–${formatCurrency(Math.max(...scenarioValues.map((item) => item.cac)))}`, "情景值"],
    ["投后贡献", `${formatCurrency(Math.min(...scenarioValues.map((item) => item.contribution)))}–${formatCurrency(Math.max(...scenarioValues.map((item) => item.contribution)))}`, "情景值"],
    ["退款率护栏", `${(Math.min(...scenarioValues.map((item) => item.refundRate)) * 100).toFixed(1)}%–${(Math.max(...scenarioValues.map((item) => item.refundRate)) * 100).toFixed(1)}%`, "情景值"],
  ] : [
    ["有效客户", "补 L2 后可测", "待测值"],
    ["客户选择率", "补 L2 后可测", "待测值"],
    ["有效 CAC", "补 L2 后可测", "待测值"],
    ["投后贡献", "补 L2 后可测", "待测值"],
    ["退款 / 留存", "补 L2 后可测", "待测值"],
  ];

  return (
    <section className="demo-stage control-stage">
      <div className="stage-kicker"><SlidersHorizontal size={16} />调整三个可控变量，形成第一版增长控制卡</div>
      <div className="stage-title-row">
        <div>
          <h1>先调整渠道动作，再看客户有没有更愿意选择</h1>
          <p>览极把预算继续拆到每个渠道真正能执行的变量，并说明这项调整会先影响什么。结果不能直接控制，但可以用阈值决定继续、修改还是停止。</p>
        </div>
        <button
          className={`l2-toggle ${l2Enabled ? "active" : ""}`}
          onClick={() => l2Available && update("l2Enabled", !control.l2Enabled)}
          disabled={!l2Available}
        >
          <Database size={14} />
          {!l2Available ? "基础诊断 · 暂不开放区间" : l2Enabled ? "L2 演示数据已载入" : "载入 L2 合成演示数据"}
        </button>
      </div>

      <div className="control-layout">
        <section className="control-inputs">
          <header><small>AI 推荐初始配置</small><strong>{selected.title}</strong></header>
          <RangeInput
            label="总测试预算"
            value={control.totalBudget}
            min={0}
            max={300000}
            step={10000}
            output={formatCurrency(control.totalBudget)}
            onChange={(value) => update("totalBudget", value)}
            ends={["不新增预算", "¥300,000"]}
          />
          <RangeInput
            label={`${combination.primary.name} / ${combination.support.name} 预算比例`}
            value={control.primaryShare * 100}
            min={20}
            max={80}
            step={5}
            output={`${Math.round(control.primaryShare * 100)}% / ${Math.round((1 - control.primaryShare) * 100)}%`}
            onChange={(value) => update("primaryShare", value / 100)}
            ends={["协同优先", "主渠道优先"]}
          />
          <RangeInput
            label="内容生产 / 付费投放比例"
            value={control.contentShare * 100}
            min={20}
            max={80}
            step={5}
            output={`${Math.round(control.contentShare * 100)}% / ${Math.round((1 - control.contentShare) * 100)}%`}
            onChange={(value) => update("contentShare", value / 100)}
            ends={["投放优先", "内容优先"]}
          />
          <div className="allocation-summary">
            <div><small>{combination.primary.name}</small><strong>{formatCurrency(result.allocation.primaryBudget)}</strong></div>
            <div><small>{combination.support.name}</small><strong>{formatCurrency(result.allocation.supportBudget)}</strong></div>
            <div><small>内容生产</small><strong>{formatCurrency(result.allocation.contentBudget)}</strong></div>
            <div><small>付费投放</small><strong>{formatCurrency(result.allocation.paidBudget)}</strong></div>
          </div>
        </section>

        <section className="control-output">
          <header>
            <div><small>{l2Enabled ? "L2 区间试算" : "L1 方向判断"}</small><h2>这次调整可能影响什么</h2></div>
            <span>{l2Enabled ? "合成演示 · 非实际结果" : "不显示伪精确数字"}</span>
          </header>
          <div className="control-metrics">
            {metricRanges.map(([label, value, status]) => (
              <article key={label}><small>{label}</small><strong>{value}</strong><span className={status === "情景值" ? "scenario" : ""}>{status}</span></article>
            ))}
          </div>
          <div className="directional-reading">
            {result.directional.map((item) => <p key={item}><ChevronRight size={13} />{item}</p>)}
          </div>
          {!l2Enabled && (
            <div className="l2-requirement">
              <Database size={18} />
              <div>
                <strong>{l2Available ? "补齐 5 类数据后，才能显示保守 / 中性 / 进取区间" : "该渠道已经恢复，可先诊断；数值推演需完成专项适配"}</strong>
                <p>{l2Available ? "两个渠道的投入、有效进入、成交客户、退款或留存、可比业务周期。" : "先补齐渠道投入、客户进入、成交结果、利润或质量护栏和归因规则。"}</p>
              </div>
            </div>
          )}
        </section>
      </div>

      <div className="section-heading channel-control-heading">
        <div><small>渠道级控制</small><h2>每次只调整少量关键动作，才能知道什么真正有用</h2></div>
        <span><SlidersHorizontal size={14} />所有数值都可以按企业能力修改</span>
      </div>
      <div className="channel-lever-workspace">
        {channelPlans.map((plan) => (
          <section key={plan.id}>
            <header>
              <div><small>{plan.role}</small><h3>{plan.channel.name}</h3></div>
              <span>先看：{plan.channel.metric}</span>
            </header>
            <p>{plan.channel.role}</p>
            <div className="lever-list">
              {plan.levers.map((item) => {
                const value = leverValue(plan.id, item);
                return (
                  <label className="channel-lever" key={item.id}>
                    <span><strong>{item.label}</strong><output>{value}{item.unit}</output></span>
                    <input
                      type="range"
                      min={item.min}
                      max={item.max}
                      step={item.step}
                      value={value}
                      onInput={(event) => updateLever(plan.id, item.id, Number(event.currentTarget.value))}
                      onChange={(event) => updateLever(plan.id, item.id, Number(event.target.value))}
                    />
                    <small><i>{item.min}{item.unit}</i><i>{item.max}{item.unit}</i></small>
                    <div className="lever-advice">
                      <p><Lightbulb size={13} /><span><b>览极建议</b>{item.recommendation}</span></p>
                      <div><span>这项调整先影响</span><strong>{item.impact}</strong><ArrowRight size={13} /><span>本批观察</span><strong>{item.watch}</strong></div>
                    </div>
                  </label>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      <div className="control-impact-chain">
        <article>
          <small>企业现在能直接调整</small>
          <strong>{channelPlans.flatMap((plan) => plan.levers.map((item) => item.label)).slice(0, 4).join("、")}等</strong>
        </article>
        <ArrowRight size={17} />
        <article>
          <small>览极先观察</small>
          <strong>{combination.primary.metric}、{combination.support.metric}</strong>
        </article>
        <ArrowRight size={17} />
        <article>
          <small>最后才判断</small>
          <strong>{objective.label}是否改善，同时{combination.support.guardrail}不恶化</strong>
        </article>
      </div>

      {l2Enabled && (
        <div className="scenario-table">
          <header><div><small>区间不是承诺</small><h2>三种情景来自明确假设</h2></div><span>比例指标均由原始分子 / 分母复算</span></header>
          <div>
            {Object.entries(result.scenarios).map(([key, item]) => (
              <article key={key}>
                <small>{item.label}</small>
                <strong>{formatNumber(item.customers)} 个有效客户</strong>
                <p>CAC {formatCurrency(item.cac)} · 退款率 {(item.refundRate * 100).toFixed(1)}%</p>
                {item.warning && <span>{item.warning}</span>}
              </article>
            ))}
          </div>
          <footer>演示假设：有效进入、选择率、单客贡献与退款率分别变化；真实项目必须由客户 L2 数据替换。</footer>
        </div>
      )}

      <div className="section-heading controllability-heading">
        <div><small>览极增长可控性图</small><h2>把可以决定、只能观察和无法控制的变量分开</h2></div>
      </div>
      <div className="controllability-map">
        <article>
          <header><SlidersHorizontal size={16} /><strong>可直接控制</strong></header>
          <p>预算、内容主题、目标人群、承接页面、响应动作</p>
          <span>写入行动卡</span>
        </article>
        <article>
          <header><LineChart size={16} /><strong>可观察，不能直接控制</strong></header>
          <p>触达、有效进入、咨询、选择率、成交与复购</p>
          <span>写入指标契约</span>
        </article>
        <article>
          <header><Globe2 size={16} /><strong>外部不可控</strong></header>
          <p>平台算法、市场需求、竞品动作与突发事件</p>
          <span>写入风险与反证</span>
        </article>
      </div>

      <div className="growth-action-card">
        <header>
          <div><small>览极 AI 根据当前选择生成 · 等待负责人确认</small><h2>下一批增长试验，建议这样做</h2></div>
          <span><UserCheck size={15} />不是自动执行</span>
        </header>
        <div className="action-card-grid">
          <article><small>这次先解决</small><strong>{objective.short}，优先验证客户在“{objective.breakpoint}”是否减少流失</strong></article>
          <article><small>{combination.primary.name}具体做</small><strong>{channelPlans[0].levers.map((item) => `${item.label} ${leverValue(channelPlans[0].id, item)}${item.unit}`).join("；")}</strong></article>
          <article><small>{combination.support.name}具体做</small><strong>{channelPlans[1].levers.map((item) => `${item.label} ${leverValue(channelPlans[1].id, item)}${item.unit}`).join("；")}</strong></article>
          <article><small>资源怎么分</small><strong>总预算 {formatCurrency(control.totalBudget)}；主 / 协同 {Math.round(control.primaryShare * 100)} / {Math.round((1 - control.primaryShare) * 100)}；内容 / 投放 {Math.round(control.contentShare * 100)} / {Math.round((1 - control.contentShare) * 100)}</strong></article>
          <article><small>做到什么才继续</small><strong>{combination.primary.metric}出现可重复改善，并且{combination.support.guardrail}没有恶化</strong></article>
          <article><small>没有改善怎么办</small><strong>保留表现较好的动作，只修改一个低效变量；不要同时加预算、换人群和改承接页</strong></article>
        </div>
        <div className="action-metric-row">
          <div><small>最终要变好</small><strong>有效客户 / 有效成交数量</strong></div>
          <div><small>客户行为要变化</small><strong>从{combination.primary.name}进入{combination.support.name}的有效选择率</strong></div>
          <div><small>执行后先看</small><strong>{combination.primary.metric}</strong></div>
          <div><small>绝对不能变坏</small><strong>{combination.support.guardrail}</strong></div>
        </div>
        <AlertBoundary label="停止条件" value={`有效 CAC 改善但${combination.support.guardrail}恶化时停止扩量并复核。`} />
        <div className="event-triggers">
          <small>何时重新判断</small>
          <span>行动状态变化</span><span>样本达到成熟条件</span><span>证据冲突</span><span>护栏触发</span><span>客户主动请求</span>
        </div>
      </div>

      <div className="final-boundary">
        <ShieldCheck size={17} />
        <p><strong>览极承诺的是判断可追溯、变量可推演、行动可观察。</strong>本页不承诺增长结果，也不会自动发布内容、调整账户或替企业执行投放。</p>
      </div>
      <div className="completion-actions">
        <button className="demo-secondary" onClick={onBack}><ArrowLeft size={16} />返回修改方案</button>
        <button className="demo-secondary" onClick={onRestart}><RefreshCw size={16} />体验另一个问题</button>
        <button className="demo-primary" onClick={() => window.print()}>导出增长控制卡 <ArrowRight size={16} /></button>
      </div>
    </section>
  );
}

function RangeInput({ label, value, min, max, step, output, onChange, ends }) {
  return (
    <label className="control-range">
      <span><strong>{label}</strong><output>{output}</output></span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onInput={(event) => onChange(Number(event.currentTarget.value))}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      <small><i>{ends[0]}</i><i>{ends[1]}</i></small>
    </label>
  );
}

function StageNavigation({ onBack, onContinue, continueLabel, disabled = false }) {
  return (
    <div className="demo-navigation">
      <button className="demo-secondary" onClick={onBack}><ArrowLeft size={16} />返回</button>
      <div><small>览极负责分析和提醒，最终选择由企业确认</small></div>
      <button className="demo-primary" onClick={onContinue} disabled={disabled}>{continueLabel}<ArrowRight size={16} /></button>
    </div>
  );
}

function AnalysisOverlay({ step, objective }) {
  const messages = [
    "正在确认三个数字属于同一段时间",
    "正在计算每个成交客户花了多少钱",
    "正在检查客户最可能在哪一步流失",
    "正在整理最值得先解决的问题",
  ];
  return (
    <div className="analysis-overlay" role="status" aria-live="polite">
      <div className="analysis-panel">
        <div className="analysis-symbol">
          <Bot size={25} />
          <i />
          <i />
        </div>
        <span>览极 AI 正在分析</span>
        <h2>{objective.label}</h2>
        <div className="analysis-progress">
          {messages.map((message, index) => (
            <div key={message} className={index <= step ? "active" : ""}>
              <span>{index < step ? <Check size={12} /> : index + 1}</span>
              <strong>{message}</strong>
            </div>
          ))}
        </div>
        <small>览极先找最值得验证的问题，不会把试算当成真实增长</small>
      </div>
    </div>
  );
}

function DemoFooter() {
  return (
    <footer className="demo-footer">
      <div className="demo-brand"><span>览极</span><i /><strong>AI 系统</strong></div>
      <p>让更多合适的客户，以更合理的成本选择你。</p>
      <span>增长决策体验 v1</span>
    </footer>
  );
}
